<template>
  <v-form ref="form" :disabled="loader.loading">
    <v-row>
      <v-col cols="3">
        <v-select
          v-model="model.event_id"
          variant="outlined"
          label="Evento"
          :items="eventInputItems"
          required
          dense
          color="orange lighten-2"
          :readonly="readonly"
          :rules="rules.event"
          density="compact"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          :model-value="model.code"
          variant="outlined"
          label="Código Cupom"
          :rules="rules.code"
          :readonly="readonly"
          density="compact"
          @update:model-value="(v) => (model.code = v.toUpperCase())"
        />
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="model.method"
          variant="outlined"
          label="Método"
          :items="[
            { title: 'Absoluto', value: 'absolute' },
            { title: 'Porcentagem', value: 'percentage' },
          ]"
          item-title="title"
          item-value="value"
          hide-details
          :readonly="readonly"
          dense
          color="orange lighten-2"
          density="compact"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-maska="
            model.method === 'absolute' ? $masks.currency : $masks.percentage
          "
          :model-value="model.value"
          variant="outlined"
          label="Valor/%"
          :rules="rules.value"
          :readonly="readonly"
          density="compact"
          @maska="(e: CustomEvent<MaskaDetail>) => model.value = Number(e.detail.unmasked)"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="3">
        <v-tooltip>
          <template #activator="{ props }">
            <v-text-field
              v-bind="props"
              v-model="model.max_items"
              type="number"
              variant="outlined"
              label="Itens por Resgate"
              :rules="rules.quantity"
              :readonly="readonly"
              density="compact"
            />
          </template>
          <div style="max-width: 400px;">
            Quantidade máxima de ingressos que o cupom afetará na compra.
          </div>
          <div style="max-width: 400px;">
            Aplicar um cupom definido com 3 itens por resgate em uma compra contendo 4 ingressos
            resultará em: 3 ingressos com desconto e 1 ingresso com o valor inalterado.
          </div>
        </v-tooltip>
      </v-col>
      <v-col cols="3">
        <v-tooltip>
          <template #activator="{ props }">
            <v-text-field
              v-bind="props"
              v-model="model.max_uses"
              type="number"
              variant="outlined"
              label="Carga Limite"
              :rules="rules.quantity"
              :readonly="readonly"
              density="compact"
            />
          </template>
          <div style="max-width: 400px;">
            Quantidade máxima de regastes deste cupom no evento.
          </div>
          <div style="max-width: 400px;">
            Um cupom com carga limite 10 poderá ser resgatado em no máximo em 10 compras no evento.
          </div>
        </v-tooltip>
      </v-col>
      <v-col cols="3">
        <date-time-field
          v-model="model.expires_at"
          :rules="rules.expires_at"
          :loading="loader.loading"
          label="Data de expiração"
        />
      </v-col>
      <v-col cols="3">
        <v-tooltip>
          <template #activator="{ props }">
            <v-checkbox
              v-bind="props"
              v-model="model.cumulative"
              label="Acumulável"
              :readonly="readonly"
              density="compact"
            />
          </template>
          <div style="max-width: 400px">
            Determina se este cupom pode ser aplicado em conjunto com outros cupons na mesma compra.
          </div>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row v-if="event && types.length">
      <v-col>
        <v-card>
          <v-card-title class="d-flex align-center ga-2">
            <span>Tipos de ingressos</span>
            <t-tooltip>
              <div style="max-width: 400px">
                O cupom poderá ser aplicado apenas em compras contendo pelo menos um dos tipos de ingresso selecionados.
              </div>
              <div style="max-width: 400px">
                Se nenhum tipo de ingresso for selecionado, o cupom poderá ser aplicado em quaisquer ingressos do evento.
              </div>
            </t-tooltip>
          </v-card-title>
          <v-card-text>
            <v-card-subtitle>
              <strong>{{ event.title }}</strong>
            </v-card-subtitle>
            <v-table>
              <thead>
                <tr>
                  <th class="text-left">
                    Setor
                  </th>
                  <th class="text-left">
                    Nome
                  </th>
                  <th class="text-left">
                    Preço
                  </th>
                  <th class="text-left">
                    Preço final
                  </th>
                  <th class="text-left">
                    Cupom ativo
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="ticketType in types"
                  :key="ticketType.id"
                >
                  <td class="text-left">
                    {{ ticketType.sector_name }}
                  </td>
                  <td class="text-left">
                    {{ ticketType.name }}
                  </td>
                  <td class="text-left">
                    {{ $formatters.currency(ticketType.latest_batch?.price ?? ticketType.price) }}
                  </td>
                  <td class="text-left">
                    {{
                      $formatters.currency(Math.max(0, (ticketType.latest_batch?.price ?? ticketType.price) - discount(ticketType.latest_batch?.price ?? ticketType.price)))
                    }}
                  </td>
                  <td class="text-center">
                    <v-checkbox
                      v-model="model.ticket_types"
                      :value="ticketType"
                      hide-details
                      multiple
                      :readonly="readonly"
                      :label="
                        model.ticket_types.find((tt) => tt.id === ticketType.id)
                          ? 'Sim'
                          : 'Não'
                      "
                    />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-show="!readonly">
      <v-col>
        <form-assign-users-coupon
          v-model="model"
          :readonly="readonly"
          :editing="editing"
          @check="u => assignedUsers = u"
        />
      </v-col>
    </v-row>

    <v-row v-if="editing || readonly">
      <v-col>
        <form-list-users-coupon
          v-model="model"
          :readonly="readonly"
          :editing="editing"
          @check="u => unassignedUsers = u"
        />
      </v-col>
    </v-row>

    <v-row v-if="!readonly" justify="end">
      <v-col cols="2">
        <v-btn
          color="primary"
          block
          :loading="loader.loading"
          @click="submit"
        >
          Salvar
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { eventName } from '@/plugins/eventName'
import ProducerService from '@/services/ProducerService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Coupon from '@/types/Coupon'
import Event from '@/types/Event'
import { ValidationRule } from '@/types/vuetify/ValidationRule'
import { DateTime, Interval } from 'luxon'
import { MaskaDetail } from 'maska'
import DateTimeField from '@/components/form/DateTimeField.vue'
import FormListUsersCoupon from './form-list-users-coupon.vue'
import FormAssignUsersCoupon from './form-assign-users-coupon.vue'
import type { VForm } from 'vuetify/lib/components'
import EventService from '@/services/EventService'
import TicketType from '@/types/TicketType'
import TTooltip from '@/components/TTooltip.vue'

const snackbar = useSnackBar()
const loader = useLoader()

const emit = defineEmits<{
  submit: [model: Coupon, users: { assigned: string[], unassigned: string[] }]
}>()

const model = defineModel<Coupon>({ required: true })

const { readonly = false, editing = false } = defineProps<{ readonly?: boolean, editing?: boolean }>()

// Events

const event = computed(() => events.value.find(event => event.id === model.value.event_id))
const events = ref<Event[]>([])
const eventInputItems = computed(() => events.value.map(event => ({ title: eventName(event), value: event.id })))

async function fetchEvents() {
  loader.increment()
  try {
    const endingInterval = Interval.fromDateTimes(DateTime.now(), DateTime.now().plus({ years: 5 })).toISO()

    const response = await (new ProducerService()).events(1, `end_between=${endingInterval}`, 100)

    events.value = response.data.data
  } catch (error) {
    snackbar.show(
      'Não foi possível carregar os tipos de ingresso',
      'error',
    )
    throw error
  } finally {
    loader.decrement()
  }
}

// Types

const typesByEventId = ref<Record<string, TicketType[]>>({})

const types = computed(() => {
  if (!event.value) return []
  return typesByEventId.value[event.value.id] ?? []
})

watch(event, async () => {
  if (!event.value) return

  if (!typesByEventId.value[event.value.id]) {
    const { data } = await fetchTypes(event.value.id)

    const paid = data.filter(t => t.latest_batch && t.latest_batch.price > 0)

    const unique = paid.reduce(
      (acc, curr) => acc.find(t => t.id === curr.id) ? acc : [...acc, curr],
      [] as TicketType[],
    )

    typesByEventId.value[event.value.id] = unique
  }

  if (typesByEventId.value[event.value.id].length === 0) {
    snackbar.show('Não há ingressos pagos configurados no evento selecionado!', 'info')
  }
})

async function fetchTypes(eventId: string) {
  try {
    loader.increment()
    return await (new EventService()).types(eventId)
  } finally {
    loader.decrement()
  }
}

// Users

const assignedUsers = ref<string[]>([])
const unassignedUsers = ref<string[]>([])

// Discounts

watch(
  () => model.value.method,
  () => model.value.value = 0,
)

function discount(price: number) {
  const value = model.value.value
  if (price === 0) return 0
  if (model.value.method === 'absolute') return Math.min(price, value / 100)
  return Math.min(price, (price * value) / 100)
}

// Validation

const form = useTemplateRef<VForm>('form')

async function validate() {
  if (!form.value) return
  const { valid } = await form.value.validate()
  return valid
}

const rules = {
  event: [ (v: string) => !!v || 'Campo obrigatório' ],
  code: [
    (v: string) => !!v || 'Campo obrigatório',
    (v: string) =>
      v.length >= 3 || 'O código deve ter no mínimo 3 caracteres',
    (v: string) =>
      /^[a-zA-Z0-9]*$/.test(v)
      || 'O código não pode conter caracteres especiais.',
  ],
  quantity: [
    (v: string) => !!v || 'Campo obrigatório',
    (v: string) =>
      parseInt(v) >= 1 || 'O valor deve ser maior ou igual a 1',
  ],
  value: [ (v: string) => !!v || 'Campo obrigatório' ],
  expires_at: [
    (v: string) => !!v || 'Campo obrigatório',
    (v: string) =>
      DateTime.fromFormat(v, 'dd/MM/yyyy HH:mm:ss').isValid
      || 'Data inválida',
  ],
} as Record<string, ValidationRule[]>

async function submit() {
  if (await validate()) {
    emit('submit', model.value, { assigned: assignedUsers.value, unassigned: unassignedUsers.value })
  }
}

onMounted(() => {
  fetchEvents()
})

</script>
